import { CheckCircle, Clock, FileText, XCircle, ShieldCheck, ShieldAlert, type LucideIcon } from 'lucide-react'
import type { ReviewStatus, DocumentStatus, AuditorStatus, BridgeLetter } from '@common/types'
import { cn } from '@frontend/lib/utils'
import { resolveComplianceStatus } from '@common/utils/validity'

export type StatusVariant = 'success' | 'warning' | 'info' | 'error' | 'neutral'

const variantStyles: Record<StatusVariant, string> = {
  success: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  warning: 'bg-amber-50 text-amber-700 border-amber-200',
  info: 'bg-blue-50 text-blue-700 border-blue-200',
  error: 'bg-red-50 text-red-700 border-red-200',
  neutral: 'bg-slate-100 text-slate-600 border-slate-200',
}

interface StatusBadgeProps {
  label: string
  variant: StatusVariant
  icon?: LucideIcon
  className?: string
}

export function StatusBadge({ label, variant, icon: Icon, className }: StatusBadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-xs font-medium',
        variantStyles[variant],
        className
      )}
    >
      {Icon && <Icon className="h-3 w-3" />}
      {label}
    </span>
  )
}

// Review status configuration
const reviewStatusConfig: Record<ReviewStatus, { variant: StatusVariant; label: string }> = {
  complete: { variant: 'success', label: 'Approved' },
  ai_complete: { variant: 'warning', label: 'Awaiting Review' },
  pending: { variant: 'neutral', label: 'Pending' },
  extracting: { variant: 'info', label: 'Extracting' },
  failed: { variant: 'error', label: 'Failed' },
}

export function ReviewStatusBadge({ status }: { status: ReviewStatus }) {
  const config = reviewStatusConfig[status] || { variant: 'neutral' as StatusVariant, label: status }
  return <StatusBadge label={config.label} variant={config.variant} />
}

// Document status configuration
const documentStatusConfig: Record<DocumentStatus, { icon: LucideIcon; variant: StatusVariant; label: string }> = {
  pending: { icon: Clock, variant: 'neutral', label: 'Pending' },
  generated: { icon: FileText, variant: 'warning', label: 'Generated' },
  uploaded: { icon: CheckCircle, variant: 'success', label: 'Uploaded' },
  failed: { icon: XCircle, variant: 'error', label: 'Failed' },
}

export function DocumentStatusBadge({ status }: { status: DocumentStatus }) {
  const config = documentStatusConfig[status]
  return <StatusBadge label={config.label} variant={config.variant} icon={config.icon} />
}

// Validity icon keyed by the resolved compliance variant. `success`/`info`
// (valid report, or a bridge letter keeping it covered) get the reassuring
// shield-check; everything else the alert shield — matching the prior mapping
// exactly for the no-bridge case (valid→check, caution/invalid/unknown→alert).
const validityVariantIcon: Record<StatusVariant, LucideIcon> = {
  success: ShieldCheck,
  info: ShieldCheck,
  warning: ShieldAlert,
  error: ShieldAlert,
  neutral: ShieldAlert,
}

export function ValidityBadge({
  auditPeriodEnd,
  bridgeLetter,
  isLatestReview,
}: {
  auditPeriodEnd: string | null | undefined
  /** Active bridge letter for this review, if any — flips the badge to the "Bridge Letter until …" state. */
  bridgeLetter?: Pick<BridgeLetter, 'startDate' | 'endDate'> | null
  /** Pass `false` for non-latest reviews to suppress the bridge label (defaults to true). */
  isLatestReview?: boolean
}) {
  const status = resolveComplianceStatus({ auditPeriodEnd, bridgeLetter: bridgeLetter ?? null, isLatestReview })
  return <StatusBadge label={status.label} variant={status.variant} icon={validityVariantIcon[status.variant]} />
}

// Auditor status configuration
const auditorStatusConfig: Record<AuditorStatus, { variant: StatusVariant; label: string }> = {
  draft: { variant: 'warning', label: 'Draft' },
  published: { variant: 'success', label: 'Published' },
  archived: { variant: 'neutral', label: 'Archived' },
}

export function AuditorStatusBadge({ status }: { status: AuditorStatus }) {
  const config = auditorStatusConfig[status] || { variant: 'neutral' as StatusVariant, label: status }
  return <StatusBadge label={config.label} variant={config.variant} />
}

// Vendor validity badge - rollup from latest review's audit period end
export function VendorValidityBadge({
  latestAuditPeriodEnd,
  reviewCount,
  bridgeLetterEndDate,
  bridgeLetterValid,
}: {
  latestAuditPeriodEnd: string | null | undefined
  reviewCount: number
  /** Compact bridge-letter rollup for the latest review (VendorListItem). */
  bridgeLetterEndDate?: string | null
  bridgeLetterValid?: boolean
}) {
  if (reviewCount === 0) {
    return <StatusBadge label="No Reviews" variant="neutral" icon={ShieldAlert} />
  }
  // VendorListItem carries only { endDate, valid } for the rollup, so synthesize
  // the { startDate, endDate } shape resolveComplianceStatus expects. The server's
  // `bridgeLetterValid` is authoritative; the far-past start keeps the letter
  // "currently valid" exactly when the server said so (its endDate is >= today).
  const bridgeLetter =
    bridgeLetterValid && bridgeLetterEndDate ? { startDate: '1970-01-01', endDate: bridgeLetterEndDate } : null
  const status = resolveComplianceStatus({ auditPeriodEnd: latestAuditPeriodEnd, bridgeLetter })
  return <StatusBadge label={status.label} variant={status.variant} icon={validityVariantIcon[status.variant]} />
}
